/**
 * La visita en curso: checklist, insumos y cierre.
 *
 * El botón grande es el que se usa con guantes puestos. El checklist se guarda
 * junto con el cierre, en una sola operación, para que no queden visitas
 * cerradas a medias.
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { dateLabelLong, VISIT_STATUS_LABEL, VISIT_STATUS_TONE, type VisitStatus } from '@cleanblue/shared';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import { haptic } from '@/lib/haptics'; // HAPTIC_FEEDBACK
import { Async, Badge, Button, Field, Modal, Note } from '@/components/ui';

interface VisitDetail {
  visit: {
    id: string; scheduled_date: string; scheduled_start: string | null; scheduled_end: string | null;
    status: VisitStatus; unit_code: string; unit_name: string | null; address: string | null;
    notes: string | null;
  };
  checklist: { id: string; label: string; ordinal: number; is_done: number; note: string | null }[];
}

export function EmployeeVisit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const query = useApi<VisitDetail>(`/employee/visits/${id}`);

  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [notes, setNotes] = useState('');
  const [consuming, setConsuming] = useState(false);

  const start = useApiMutation(
    () => api.post(`/employee/visits/${id}/start`),
    ['/employee/visits', '/employee/home'],
  );

  const finish = useApiMutation(
    () => api.post(`/employee/visits/${id}/finish`, {
      notes: notes || undefined,
      checklist: Object.entries(checked).map(([itemId, isDone]) => ({ id: itemId, is_done: isDone })),
    }),
    ['/employee/visits', '/employee/home'],
    { onSuccess: () => { haptic.success(); navigate('/empleado'); } }, // HAPTIC_FEEDBACK
  );

  return (
    <Async query={query}>
      {({ visit: v, checklist }) => (
        <>
          <section className="msec">
            <div className="mcard mcard--hero">
              <div className="mrow" style={{ paddingTop: 0, borderBottom: 0 }}>
                <span>
                  <strong style={{ fontSize: 18 }}>{v.unit_code}</strong>
                  <span className="msub">
                    {v.unit_name ?? v.address ?? ''}
                  </span>
                </span>
                <Badge tone={VISIT_STATUS_TONE[v.status]}>{VISIT_STATUS_LABEL[v.status]}</Badge>
              </div>
              <p className="small muted mt-1">
                {dateLabelLong(v.scheduled_date)}
                {v.scheduled_start && ` · ${v.scheduled_start}`}
                {v.scheduled_end && `–${v.scheduled_end}`}
              </p>

              {v.status === 'SCHEDULED' && (
                <Button variant="primary" size="lg" block disabled={start.isPending}
                  onClick={() => start.mutate()}>
                  {start.isPending ? 'Iniciando…' : 'Empezar la limpieza'}
                </Button>
              )}
            </div>

            {(start.error || finish.error) && (
              <Note tone="danger">{(start.error ?? finish.error)!.message}</Note>
            )}
          </section>

          {v.notes && (
            <section className="msec">
              <div className="msec__title">Indicaciones</div>
              <div className="mcard"><p className="small" style={{ margin: 0 }}>{v.notes}</p></div>
            </section>
          )}

          {checklist.length > 0 && (
            <section className="msec">
              <div className="msec__title">Checklist</div>
              <div className="mcard">
                {checklist.map((item, i) => (
                  <label
                    key={item.id}
                    className="mrow"
                    style={{
                      borderBottom: i === checklist.length - 1 ? 0 : undefined,
                      cursor: 'pointer',
                    }}
                  >
                    <span className="mrow__l" style={{ color: 'var(--ink)' }}>{item.label}</span>
                    <input
                      type="checkbox"
                      style={{ width: 20, height: 20, accentColor: 'var(--blue-600)' }}
                      checked={checked[item.id] ?? item.is_done === 1}
                      onChange={(e) => {
                        haptic.light(); // HAPTIC_FEEDBACK
                        setChecked((prev) => ({ ...prev, [item.id]: e.target.checked }));
                      }}
                    />
                  </label>
                ))}
              </div>
            </section>
          )}

          <section className="msec">
            <div className="msec__title">Insumos usados</div>
            <div className="mcard">
              <Button block onClick={() => setConsuming(true)}>Registrar consumo</Button>
            </div>
          </section>

          {v.status === 'IN_PROGRESS' && (
            <section className="msec">
              <div className="mcard">
                <Field label="Observaciones">
                  <textarea className="input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
                </Field>
                <Button variant="primary" size="lg" block disabled={finish.isPending}
                  onClick={() => finish.mutate()}>
                  {finish.isPending ? 'Cerrando…' : 'Terminar la visita'}
                </Button>
              </div>
            </section>
          )}

          {consuming && <ConsumptionModal visitId={v.id} onClose={() => setConsuming(false)} />}
        </>
      )}
    </Async>
  );
}

function ConsumptionModal({ visitId, onClose }: { visitId: string; onClose: () => void }) {
  const items = useApi<{ items: { id: string; name: string; consumption_unit: string; stock: number }[] }>(
    '/employee/inventory',
  );
  const [itemId, setItemId] = useState('');
  const [quantity, setQuantity] = useState('1');

  const mutation = useApiMutation(
    () => api.post('/employee/consumption', { item_id: itemId, quantity, visit_id: visitId }),
    ['/employee/inventory'],
    { onSuccess: onClose },
  );

  return (
    <Modal
      title="Registrar consumo"
      onClose={onClose}
      footer={(
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" disabled={!itemId || mutation.isPending} onClick={() => mutation.mutate()}>
            Registrar
          </Button>
        </>
      )}
    >
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}

      <Field label="Producto" required>
        <select className="select" value={itemId} onChange={(e) => setItemId(e.target.value)}>
          <option value="">Elegir…</option>
          {(items.data?.items ?? []).map((i) => (
            <option key={i.id} value={i.id}>
              {i.name} ({i.stock} {i.consumption_unit})
            </option>
          ))}
        </select>
      </Field>

      <Field label="Cantidad" required>
        <input className="input input--money" type="number" step="0.5" min="0.5"
          value={quantity} onChange={(e) => setQuantity(e.target.value)} />
      </Field>
    </Modal>
  );
}
